
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Bot, Zap, Loader2 } from 'lucide-react';
import { OnboardingStepProps } from './types';

export interface AgentActivationStepProps extends OnboardingStepProps {}

export const AgentActivationStep: React.FC<AgentActivationStepProps> = ({
  onComplete,
  onSkip,
  isLoading = false
}) => {
  const agentTypes = [
    {
      name: 'Candidate Screening Agent',
      description: 'Automatically screen and evaluate candidates',
      status: 'ready'
    },
    {
      name: 'Email Processing Agent',
      description: 'Process and categorize incoming emails',
      status: 'ready'
    },
    {
      name: 'Orchestrator Agent',
      description: 'Coordinate between different AI agents',
      status: 'ready'
    },
    {
      name: 'Calendar Management Agent',
      description: 'Schedule and manage calendar events',
      status: 'ready'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">AI Agents Ready!</h2>
        <p className="text-muted-foreground">
          Your AI agents have been automatically provisioned and are ready to help you
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Available AI Agents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {agentTypes.map((agent, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <h4 className="font-medium">{agent.name}</h4>
                    <p className="text-sm text-muted-foreground">{agent.description}</p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <Zap className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Bot className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">What's Next?</h4>
            <p className="text-sm text-blue-700 mt-1">
              Your AI agents are now active and will start helping you with tasks like candidate screening, 
              email processing, and calendar management. You can configure and customize them in Settings.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        {onSkip && (
          <Button variant="outline" onClick={onSkip} disabled={isLoading}>
            Continue without configuring
          </Button>
        )}
        <Button onClick={onComplete} disabled={isLoading} className="flex-1">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Activating...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </div>
    </div>
  );
};
